// Metas Entrada a Pits (coordinador) — configuración del indicador.
//
// Lista de causales PARTICIPANTES con su meta default (min/evento) editable +
// overrides por línea (grid línea × meta; celda vacía = usa el default).
// Agregar una causal del catálogo la vuelve participante; quitarla la saca
// del indicador (el histórico de paros no se toca — solo se dejan de mostrar).
//
// Guardar hace PUT con RECONCILIACIÓN TOTAL: el estado editado reemplaza la
// configuración completa del tenant (patrón C10 skus/matriz).

import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { CoordinadorMetasPits, MetasPitsPut } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { useCoordinadorMetasPits, useGuardarMetasPits } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';

// Estado editable: strings crudos de los inputs (se validan al guardar).
type ParticipanteEdit = {
  causal_id: string;
  meta_default: string;
  /** linea_id → meta (string del input). Ausente/vacío = usa el default. */
  overrides: Record<string, string>;
};

/** Construye el estado editable desde las filas crudas del backend. */
function estadoDesdeMetas(metas: CoordinadorMetasPits['metas']): ParticipanteEdit[] {
  const porCausal = new Map<string, ParticipanteEdit>();
  // Primero los defaults (definen participación)…
  for (const m of metas) {
    if (m.linea_id === null) {
      porCausal.set(m.causal_id, {
        causal_id: m.causal_id,
        meta_default: String(m.meta_minutos),
        overrides: {},
      });
    }
  }
  // …después los overrides de causales participantes.
  for (const m of metas) {
    if (m.linea_id !== null) {
      const p = porCausal.get(m.causal_id);
      if (p) p.overrides[m.linea_id] = String(m.meta_minutos);
    }
  }
  return [...porCausal.values()];
}

export function MetasPitsPage() {
  const { state } = useAuth();
  const { data, isLoading, isError, error } = useCoordinadorMetasPits();
  const guardar = useGuardarMetasPits();

  const [participantes, setParticipantes] = useState<ParticipanteEdit[]>([]);
  const [dirty, setDirty] = useState(false);
  const [causalNueva, setCausalNueva] = useState('');

  // Sincronizar el estado editable con el server SOLO mientras no haya
  // edición en curso (evita pisar cambios del usuario en un refetch).
  useEffect(() => {
    if (data && !dirty) {
      setParticipantes(estadoDesdeMetas(data.metas));
    }
  }, [data, dirty]);

  const causalById = useMemo(() => {
    const m = new Map<string, CoordinadorMetasPits['causales'][number]>();
    for (const c of data?.causales ?? []) m.set(c.id, c);
    return m;
  }, [data]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando metas…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  const idsParticipantes = new Set(participantes.map((p) => p.causal_id));
  const causalesDisponibles = data.causales.filter((c) => !idsParticipantes.has(c.id));

  const mutar = (fn: (prev: ParticipanteEdit[]) => ParticipanteEdit[]) => {
    setParticipantes(fn);
    setDirty(true);
  };

  const agregarParticipante = () => {
    if (!causalNueva) return;
    mutar((prev) => [...prev, { causal_id: causalNueva, meta_default: '', overrides: {} }]);
    setCausalNueva('');
  };

  const quitarParticipante = (causalId: string) => {
    mutar((prev) => prev.filter((p) => p.causal_id !== causalId));
  };

  const setMetaDefault = (causalId: string, v: string) => {
    mutar((prev) => prev.map((p) => (p.causal_id === causalId ? { ...p, meta_default: v } : p)));
  };

  const setOverride = (causalId: string, lineaId: string, v: string) => {
    mutar((prev) =>
      prev.map((p) =>
        p.causal_id === causalId ? { ...p, overrides: { ...p.overrides, [lineaId]: v } } : p,
      ),
    );
  };

  const onGuardar = () => {
    // Validación de inputs antes del PUT.
    const body: MetasPitsPut = { participantes: [] };
    for (const p of participantes) {
      const causal = causalById.get(p.causal_id);
      const nombre = causal ? causal.nombre : p.causal_id;
      const def = Number(p.meta_default);
      if (!p.meta_default.trim() || !Number.isFinite(def) || def <= 0) {
        toast.error(`Ingrese una meta default válida (> 0 minutos) para ${nombre}.`);
        return;
      }
      const overrides: Array<{ linea_id: string; meta_minutos: number }> = [];
      for (const [lineaId, valor] of Object.entries(p.overrides)) {
        if (!valor.trim()) continue; // vacío = usa el default
        const n = Number(valor);
        if (!Number.isFinite(n) || n <= 0) {
          toast.error(`Override inválido (> 0 minutos) en ${nombre}.`);
          return;
        }
        overrides.push({ linea_id: lineaId, meta_minutos: n });
      }
      body.participantes.push({
        causal_id: p.causal_id,
        meta_minutos_default: def,
        overrides,
      });
    }

    guardar.mutate(body, {
      onSuccess: () => {
        setDirty(false);
        toast.success('Metas de Entrada a Pits guardadas.');
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'No pudimos guardar las metas.');
      },
    });
  };

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Metas Entrada a Pits</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {data.tenant.nombre} · minutos promedio por evento de paro · meta default por causal +
              override por línea
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <Badge tone="warn" className="normal-case tracking-normal">
                Cambios sin guardar
              </Badge>
            )}
            <Button variant="primary" onClick={onGuardar} loading={guardar.isPending}>
              Guardar cambios
            </Button>
          </div>
        </header>

        {/* Agregar causal participante */}
        <Card>
          <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="pits-causal-nueva">Agregar causal al indicador</Label>
              <Select
                id="pits-causal-nueva"
                value={causalNueva}
                onChange={(e) => setCausalNueva(e.target.value)}
              >
                <option value="">Seleccione una causal…</option>
                {causalesDisponibles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                    {c.causal_madre_nombre ? ` (${c.causal_madre_nombre})` : ''}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="outline" disabled={!causalNueva} onClick={agregarParticipante}>
              <Icon name="plus" className="size-3.5" />
              Agregar
            </Button>
          </CardContent>
        </Card>

        {participantes.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-sm font-medium text-fg">Sin causales participantes</p>
              <p className="mt-1 text-sm text-fg-secondary">
                Agregue al menos una causal (ej. Cambio de Rollo) y defina su meta de minutos por
                evento para activar el indicador Entrada a Pits.
              </p>
            </CardContent>
          </Card>
        ) : (
          participantes.map((p) => {
            const causal = causalById.get(p.causal_id);
            return (
              <Card key={p.causal_id}>
                <CardContent className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-fg">
                        {causal ? causal.nombre : p.causal_id}
                      </p>
                      {causal?.causal_madre_nombre && (
                        <p className="text-[11px] text-fg-tertiary">
                          Causal madre: {causal.causal_madre_nombre}
                        </p>
                      )}
                    </div>
                    <div className="flex items-end gap-2">
                      <div>
                        <Label htmlFor={`meta-default-${p.causal_id}`}>
                          Meta default (min/evento)
                        </Label>
                        <Input
                          id={`meta-default-${p.causal_id}`}
                          type="number"
                          min="0.5"
                          step="0.5"
                          className="w-36 tabular-nums"
                          value={p.meta_default}
                          onChange={(e) => setMetaDefault(p.causal_id, e.target.value)}
                          placeholder="ej. 4"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-crit"
                        title="Quitar la causal del indicador (no borra paros)"
                        onClick={() => quitarParticipante(p.causal_id)}
                      >
                        <Icon name="trash" className="size-3.5" />
                        Quitar
                      </Button>
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                      Overrides por línea (vacío = usa el default)
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                      {data.lineas.map((l) => {
                        const valor = p.overrides[l.id] ?? '';
                        return (
                          <div key={l.id}>
                            <Label
                              htmlFor={`ov-${p.causal_id}-${l.id}`}
                              className="truncate"
                              title={`${l.codigo} · ${l.nombre}`}
                            >
                              {l.codigo}
                            </Label>
                            <Input
                              id={`ov-${p.causal_id}-${l.id}`}
                              type="number"
                              min="0.5"
                              step="0.5"
                              className={cn('tabular-nums', valor && 'border-accent')}
                              value={valor}
                              placeholder={p.meta_default || '—'}
                              onChange={(e) => setOverride(p.causal_id, l.id, e.target.value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        <p className="text-[11px] text-fg-tertiary">
          El indicador se consulta en Análisis de paros → pestaña “Entrada a Pits”. Quitar una
          causal solo la saca del indicador; los paros registrados no se modifican.
        </p>
      </div>
    </RoleLayout>
  );
}
